// externals
import type { ReactNode } from 'react';

// components
import { Box } from '@interstate/components/Box';
import { Grid } from '@interstate/components/Grid';
import {
    VehicleTitle,
    VinStockRow,
    ExteriorInteriorColor,
    DrivetrainDescription,
    SellingPrice,
    AgeOfInventory,
    MilesOdometer,
    CompactVehicleImage
} from './components';
import { useInterstateTheme } from '@interstate/components/InterstateThemeProvider';
import { useMediaQuery } from 'react-responsive';
import type { useVehicleListRowItemInStock } from '../useVehicleListRowItemInStock';
import { getCompactGridBreakpoints } from './getCompactGridBreakpoints';

type VehicleListRowItemInStockCompactSelectedWithoutInputsProps = {
    vehicleData: ReturnType<typeof useVehicleListRowItemInStock>;
    forceSmallBreakpoint?: boolean;
    /** Rendered beside the vehicle details at the right end of the row, wrapping below them when the row is narrow. */
    inlineActions?: ReactNode;
};

export const VehicleListRowItemInStockCompactSelectedWithoutInputs = ({
    vehicleData,
    forceSmallBreakpoint,
    inlineActions
}: VehicleListRowItemInStockCompactSelectedWithoutInputsProps) => {
    const {
        imageUrl,
        year,
        make,
        model,
        vin,
        stockNumber,
        mileage,
        exteriorColor,
        interiorColor,
        driveLine,
        vehicleListItemTitle,
        formattedPrice,
        ageDays,
        shouldShowAgeOfInventory
    } = vehicleData;

    const theme = useInterstateTheme();
    // `maxWidth` is inclusive while the details grid's `xl` column is min-width, so at exactly xl both would be true —
    // details in columns and actions on their own line at once. Stop one pixel short, as MUI's own `down('xl')` does.
    const isBreakpointDownXl = useMediaQuery({ maxWidth: theme.breakpoints.values.xl - 1 });

    // Below xl the vehicle details stack, which leaves the actions a line of their own — the full width of the row, so
    // they run along it side by side. Beside the details they stack instead.
    const actionsOnOwnLine = forceSmallBreakpoint || isBreakpointDownXl;

    return (
        <Grid container spacing={1} alignItems="center" flexGrow={1}>
            <Grid xs="auto">
                <CompactVehicleImage vehicle={{ imageUrl, year, make, model }} />
            </Grid>
            {/* Only becomes a flex row when there are actions to sit beside the details, so a row without them keeps the
                plain block layout — and the 10-of-12 columns — it has always had. With actions it takes the rest of the
                row instead, so they reach its right edge, and it does not wrap while the details are in their own
                columns: the details give up width rather than pushing the actions onto a line below. */}
            <Grid
                xs={inlineActions ? true : 10}
                {...(inlineActions
                    ? {
                          display: 'flex',
                          flexWrap: actionsOnOwnLine ? 'wrap' : 'nowrap',
                          alignItems: 'center',
                          columnGap: 2,
                          rowGap: 1,
                          flexGrow: 1
                      }
                    : {})}
            >
                <Grid container spacing={1} alignItems="center" flexGrow={1} minWidth={0}>
                    <Grid
                        {...getCompactGridBreakpoints({ xs: 12, xl: 4, forceSmallBreakpoint })}
                        display="flex"
                        gap={2}
                        flexWrap="nowrap"
                    >
                        <Box display="flex" flexDirection="column" gap={1}>
                            <VehicleTitle title={vehicleListItemTitle} />
                            <ExteriorInteriorColor exteriorColor={exteriorColor} interiorColor={interiorColor} />
                        </Box>
                    </Grid>
                    <Grid
                        {...getCompactGridBreakpoints({ xs: 12, xl: 5, forceSmallBreakpoint })}
                        display="flex"
                        flexDirection="column"
                        gap={1}
                    >
                        <VinStockRow vin={vin} stockNumber={stockNumber} />
                        <DrivetrainDescription driveLine={driveLine} />
                    </Grid>
                    <Grid
                        {...getCompactGridBreakpoints({ xs: 12, xl: 3, forceSmallBreakpoint })}
                        display="flex"
                        flexDirection="column"
                        gap={1}
                    >
                        <SellingPrice formattedPrice={formattedPrice} />
                        {shouldShowAgeOfInventory && <AgeOfInventory ageDays={ageDays} />}
                        <MilesOdometer mileage={mileage} />
                    </Grid>
                </Grid>
                {inlineActions && (
                    // Beside the details: stacked at content width, each action as wide as the wider one. On a line of
                    // its own: side by side across it. A row showing its inputs is always side by side — see the
                    // sibling with-inputs component.
                    <Box
                        sx={{
                            marginLeft: 'auto',
                            display: 'flex',
                            flexWrap: 'wrap',
                            justifyContent: 'flex-end',
                            gap: 1,
                            flexDirection: actionsOnOwnLine ? 'row' : 'column',
                            width: actionsOnOwnLine ? '100%' : 'auto',
                            '& > *': { flex: '1 1 auto' }
                        }}
                    >
                        {inlineActions}
                    </Box>
                )}
            </Grid>
        </Grid>
    );
};
